import React, { FC, useEffect, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import cx from 'clsx';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCellHead from './TableCellHead';
import SortIcon from 'components/@common/Table/TableSortIcon';
import TableHead from '@material-ui/core/TableHead';
import TableCell from '@material-ui/core/TableCell';
import { TableRowProps } from '@material-ui/core/TableRow';
import TableDataRow from './TableRow';
import LazyLoadingList from '../ListUtilities/LazyLoadingList';
import TableSummaryRow from './TableSummaryRow';

const LIMIT = 50;

const useStyles = makeStyles<Theme>((theme: Theme) => {
  return {
    iconColor: {
      color: theme.palette.secondary.main
    },
    headCell: {
      width: '200px',
      paddingLeft: 0,
      paddingRight: 0,
      borderBottom: 'none',
      userSelect: 'none',
      verticalAlign: 'top',
      '@media print': { verticalAlign: 'bottom' }
    },
    checkboxCell: {
      width: '0.2rem',
      padding: 0,
      paddingLeft: theme.spacing(1)
    },
    firstCell: {
      paddingRight: 0,
      paddingLeft: theme.spacing(1.5)
    },
    body: {
      boxShadow:
        '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 1px rgba(0,0,0,0.14), 0px 1px 3px 1px rgba(0,0,0,0.12)',
      borderRadius: theme.shape.borderRadius
    },
    iconCell: {
      width: '65px'
    },
    table: {
      margin: `0 auto ${theme.spacing(5)}px`
    },
    clickable: {
      cursor: 'pointer'
    }
  };
});

export enum SortDir {
  ASC = 'ASC',
  DESC = 'DESC'
}

type SortOrder = {
  direction?: SortDir;
  sortKey: string | null;
  sortFn?: (a: any, b: any) => number;
};

const sortByKey = (
  direction: SortOrder['direction'],
  sortKey: SortOrder['sortKey'],
  a: any,
  b: any
): number => {
  if (sortKey) {
    const keyA = a[sortKey];
    const keyB = b[sortKey];
    return (typeof keyA === 'string' &&
      typeof keyB === 'string' &&
      keyA.toUpperCase() < keyB.toUpperCase()) ||
      (typeof keyA === 'number' && typeof keyB === 'number' && keyA < keyB)
      ? -1
      : 1;
  }
  return 0;
};

export interface ColumnProps<T> {
  id: string;
  label: React.ReactNode;
  helpText?: string;
  sortable?: boolean;
  getValue: (item: T, idx: number) => any;
  sortKey?: string;
  sortFn?: (a: any, b: any) => number;
  className?: string;
  summaryKey?: string;
  summaryLabel?: string;
}

export interface TableProps<T> {
  data: Array<T>;
  columns: ColumnProps<T>[];
  checkboxMap?: { [key: string]: boolean };
  onCheckboxChange?: React.ChangeEventHandler<HTMLInputElement>;
  onRowClick?: (item: T, idx: number) => void;
  options?: {
    defaultSort?: { sortKey: string; direction?: SortDir };
    checkbox?: boolean;
    sortable?: boolean;
    readOnly?: boolean;
  };
  classes?: {
    table?: () => string;
    row?: (item: T, idx: number) => string;
    body?: () => string;
    headRow?: (column: ColumnProps<T>, idx: number) => string;
  };
  rowProps?: (item: T, idx: number) => TableRowProps;
  lazyLoadingLimit?: number;
}

const LwtTable: FC<TableProps<any>> = ({
  lazyLoadingLimit,
  checkboxMap,
  onCheckboxChange,
  onRowClick,
  columns,
  data,
  options,
  classes: propClasses = {
    table: () => '',
    row: () => '',
    body: () => '',
    headRow: () => ''
  },
  rowProps
}) => {
  const classes = useStyles();
  const [forceUpdateList, setForceUpdateList] = useState(false);

  const [sort, setSort] = React.useState<SortOrder>({
    direction: options?.defaultSort?.direction ?? SortDir.ASC,
    sortKey: options?.defaultSort?.sortKey ?? columns?.find(col => col.sortKey)?.sortKey ?? null
  });

  const handleSort = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>, sortFn: (a: any, b: any) => number): void => {
      const { sortKey: activeSortKey, direction } = sort;
      setSort({
        direction:
          event.currentTarget &&
          event.currentTarget.id === activeSortKey &&
          direction === SortDir.ASC
            ? SortDir.DESC
            : SortDir.ASC,
        sortKey: event.currentTarget.id,
        sortFn
      });
    },
    [setSort, sort]
  );

  const handleRowClick = (item: any, idx: number) => (
    event?: React.MouseEvent<HTMLDivElement | HTMLTableRowElement | Element>
  ) => {
    if (event) {
      event.stopPropagation();
    }

    if (onRowClick) {
      onRowClick(item, idx);
    }
  };

  useEffect(() => {
    if (sort.direction && sort.sortKey) {
      data.sort((a, b) =>
        sort.sortFn ? sort.sortFn(a, b) : sortByKey(sort.direction, sort.sortKey, a, b)
      );
      if (sort.direction === SortDir.DESC) data.reverse();
      setForceUpdateList(true);
    }
  }, [sort, data]);

  // ensures 'forceUpdateList' only runs once
  useEffect(() => {
    if (forceUpdateList) {
      setForceUpdateList(false);
    }
  }, [forceUpdateList]);

  return (
    <Table className={cx(classes.table, propClasses?.table?.() ?? '')}>
      <TableHead>
        <TableRow>
          {options?.checkbox && (
            <TableCell className={cx(classes.headCell, classes.checkboxCell)} />
          )}
          {columns.map(({ id, label, helpText, sortable = false, sortKey, ...col }, idx) => {
            const sortProps = {
              innerContainerProps: { id: sortKey || id },
              onClick: handleSort,
              sortFn: col.sortFn,
              SortIcon: (
                <SortIcon
                  className={classes.iconColor}
                  direction={sort.sortKey === sortKey ? sort.direction : undefined}
                />
              )
            };
            return (
              <TableCellHead
                key={`${id}-${idx}-table-header`}
                helpText={helpText}
                className={cx(classes.headCell, classes.firstCell, sortable && classes.clickable)}
                typographyProps={{
                  className: propClasses?.headRow?.({ ...col, id, label, sortable }, idx) ?? ''
                }}
                {...(sortable && sortProps)}
              >
                {label}
              </TableCellHead>
            );
          })}
        </TableRow>
      </TableHead>
      <TableBody className={cx(classes.body, propClasses?.body?.() ?? '')}>
        {columns.some(col => col.summaryLabel || col.summaryKey) && (
          <TableSummaryRow data={data} columns={columns} />
        )}
        <LazyLoadingList
          data={data}
          forceUpdate={forceUpdateList}
          limit={lazyLoadingLimit || LIMIT}
          renderItem={({ index: idx, item }) => (
            <TableDataRow
              id={item.id}
              key={`${item.id}-remove-table-row_${idx}`}
              label={`${item.label} Row`}
              readOnly={options?.readOnly || false}
              enableCheck={options?.checkbox || false}
              noCellBorder={idx === data.length - 1}
              checked={checkboxMap ? checkboxMap[item.id] : false}
              rowData={item}
              rowDataIndex={idx}
              handleCheck={onCheckboxChange}
              handleRowClick={handleRowClick(item, idx)}
              className={cx(
                !!onRowClick && classes.clickable,
                propClasses?.row?.(item, idx) ?? '',
                item.className
              )}
              columns={columns}
              rowProps={rowProps}
            />
          )}
        />
      </TableBody>
    </Table>
  );
};

export default LwtTable;
